// frontend/src/components/ui/RecentCalculationsModal.tsx
import React, { useState, useEffect } from 'react';
import { History, X, Trash2, ChevronDown, ChevronUp, Package, ShieldCheck, Clock } from 'lucide-react';
import { getRecentCalculations, clearOfflineCalculations, type OfflineCalculation } from '../../utils/offlineStorage';
import { hapticFeedback } from '../../utils/nativeBridge';
import { useLanguage } from '../../contexts/LanguageContext';

interface RecentCalculationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const RecentCalculationsModal: React.FC<RecentCalculationsModalProps> = ({
    isOpen,
    onClose
}) => {
    const [calculations, setCalculations] = useState<OfflineCalculation[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const { lang } = useLanguage();
    const isTurkish = lang === 'tr';

    const loadData = async () => {
        const data = await getRecentCalculations();
        setCalculations(data);
    };

    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleClearAll = async () => {
        if (window.confirm(isTurkish ? 'Son hesaplama geçmişini silmek istediğinize emin misiniz?' : 'Are you sure you want to clear recent calculation history?')) {
            hapticFeedback('heavy');
            await clearOfflineCalculations();
            setCalculations([]);
        }
    };

    const toggleExpand = (id: string) => {
        hapticFeedback('light');
        setExpandedId(prev => prev === id ? null : id);
    };

    return (
        <div className="fixed inset-0 z-[130] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg sm:max-w-xl bg-white dark:bg-[#0D111C] rounded-3xl p-4 sm:p-6 shadow-2xl border border-slate-200 dark:border-white/10 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
                {/* Top Header */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-orange-500/10 text-orange-500 flex items-center justify-center">
                            <History className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 dark:text-white text-base sm:text-lg">
                                    {isTurkish ? 'Son 5 Hesaplama' : 'Recent 5 Calculations'}
                                </h3>
                                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300">
                                    {isTurkish ? 'Salt Okunur' : 'Read-Only'}
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {isTurkish 
                                    ? 'Cihazınızda saklanan son başarılı hesaplama kayıtları' 
                                    : 'Recent successful calculation snapshots saved on this device'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {calculations.length > 0 && (
                            <button
                                onClick={handleClearAll}
                                className="p-2 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-colors"
                                title={isTurkish ? 'Tümünü Temizle' : 'Clear All'}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-colors"
                            title={isTurkish ? 'Kapat' : 'Close'}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content List */}
                <div className="flex-1 overflow-y-auto my-4 space-y-2.5 pr-1">
                    {calculations.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-3">
                                <Clock className="w-6 h-6 text-slate-400" />
                            </div>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                {isTurkish ? 'Kayıtlı hesaplama bulunamadı' : 'No recent calculations found'}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
                                {isTurkish 
                                    ? 'Yaptığınız hesaplamalar otomatik olarak buraya kaydedilecektir.' 
                                    : 'Successful calculations will be saved here automatically.'}
                            </p>
                        </div>
                    ) : (
                        calculations.map((calc, index) => {
                            const isExpanded = expandedId === calc.id;
                            const date = new Date(calc.timestamp).toLocaleDateString(
                                isTurkish ? 'tr-TR' : 'en-US',
                                { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
                            );
                            const inputs = (calc.inputs || {}) as Record<string, any>;
                            const dims = inputs.dimensions || {};

                            return (
                                <div
                                    key={calc.id || index}
                                    className="border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden bg-slate-50/50 dark:bg-white/[0.02] hover:border-orange-500/40 transition-colors"
                                >
                                    <div
                                        onClick={() => toggleExpand(calc.id)}
                                        className="p-3.5 flex items-center justify-between cursor-pointer select-none"
                                    >
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center shrink-0 mt-0.5">
                                                <Package className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                                                        {calc.summary || (isTurkish ? 'Hesaplama Snapshot' : 'Calculation Snapshot')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                                    <Clock className="w-3 h-3" />
                                                    <span>{date}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Expanded Read-Only Details */}
                                    {isExpanded && (
                                        <div className="px-4 pb-4 pt-2 border-t border-slate-200/60 dark:border-white/5 bg-white/60 dark:bg-white/[0.01] space-y-3 text-xs">
                                            <div className="grid grid-cols-2 gap-2 text-slate-600 dark:text-slate-300">
                                                <div className="p-2 rounded-xl bg-slate-100/70 dark:bg-white/5">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">
                                                        {isTurkish ? 'Ağırlık' : 'Weight'}
                                                    </span>
                                                    <span className="font-bold text-slate-900 dark:text-white text-sm">
                                                        {inputs.weight ? `${inputs.weight} Ton` : '-'}
                                                    </span>
                                                </div>
                                                <div className="p-2 rounded-xl bg-slate-100/70 dark:bg-white/5">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">
                                                        {isTurkish ? 'Ölçüler (B×E×Y)' : 'Dimensions (L×W×H)'}
                                                    </span>
                                                    <span className="font-bold text-slate-900 dark:text-white text-sm">
                                                        {dims.length || dims.boy || '-'} × {dims.width || dims.en || '-'} × {dims.height || dims.yukseklik || '-'} m
                                                    </span>
                                                </div>
                                                <div className="p-2 rounded-xl bg-slate-100/70 dark:bg-white/5">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">
                                                        {isTurkish ? 'Taşıma Ünitesi' : 'Transport Unit'}
                                                    </span>
                                                    <span className="font-medium text-slate-900 dark:text-white">
                                                        {inputs.transportUnit || '-'}
                                                    </span>
                                                </div>
                                                <div className="p-2 rounded-xl bg-slate-100/70 dark:bg-white/5">
                                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider block font-semibold">
                                                        {isTurkish ? 'Taşıma Modu' : 'Transport Mode'}
                                                    </span>
                                                    <span className="font-medium text-slate-900 dark:text-white">
                                                        {inputs.transportMode || '-'}
                                                    </span>
                                                </div>
                                            </div>

                                            {inputs.frictionSurface && (
                                                <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-slate-100/50 dark:bg-white/5 text-slate-500 dark:text-slate-400">
                                                    <span>{isTurkish ? 'Sürtünme Yüzeyi' : 'Friction Surface'}:</span>
                                                    <span className="font-medium text-slate-800 dark:text-slate-200">{inputs.frictionSurface}</span>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium text-[11px] pt-1">
                                                <ShieldCheck className="w-3.5 h-3.5" />
                                                <span>{isTurkish ? 'Kayıt Başarıyla Tamamlandı (IMO CSS Uyumlu)' : 'Calculation Certified (IMO CSS Compliant)'}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="pt-3 border-t border-slate-100 dark:border-white/10 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold text-xs hover:opacity-90 transition-opacity"
                    >
                        {isTurkish ? 'Kapat' : 'Close'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecentCalculationsModal;
